import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import MoreMenu, { ColorPicker, swatch } from './MoreMenu';
import TodoEditor from './TodoEditor';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * ONE PLACE FOR A TO-DO (Oct 2026, todo-ui-proposal.md 3.5, Phase 2).
 *
 * Before, a to-do had three surfaces: the inline editor under its row (the
 * fields), the comments modal (checklist, files, client, flag,
 * dependencies, AI, comments) and the board's card popup (its sub-tasks).
 * People could not guess which held what. This panel is all three: on a
 * desk it slides in at the right and the list stays in sight - J/K or the
 * arrows step to the next to-do - and on a phone it is a full-height sheet.
 *
 *   header    the ring, the place it lives (list › section, its parent),
 *             previous / next, a ⋯ (pin, colour, convert to note, complete
 *             forever, delete) and close
 *   fields    TodoEditor in its panel shape - title, date and time,
 *             deadline, how long, priority, list and section, labels,
 *             client, ticket, notes, repeat, reminders - saved with Save
 *             (Ctrl+Enter), and saved for you when the panel closes or
 *             moves on, so nothing typed is lost
 *   sub-tasks the tree the list shows (CardPopup's SubtaskTree)
 *   the rest  TodoDetail inline: checklist and files, who has it, flag,
 *             what it waits on and holds up, AI help, comments, activity
 *
 * A to-do handed to somebody else (Delegated) or one already done shows
 * the conversation only. Escape closes; the focus goes back to its row.
 * The page does every call; `on` says what was asked for.
 *
 * FILES DROPPED on the panel, or a picture PASTED anywhere in it (into the
 * notes, say), go to `onFiles` - the same upload the Attach row uses
 * (TodoParts.js), so a to-do takes a file the way a note does.
 */
export default function TodoPanel({
  item, list, parent = null, phone = false, editable = true, editorProps, detail, subtasks = null,
  position = null, liveRef, on, menu = [], scrollTo = null, onFiles = null
}) {
  const box = useRef(null);
  const [dropping, setDropping] = useState(false);
  const filesOf = (dt) => [...(dt?.files || [])].filter((f) => f && f.size >= 0);
  const dropProps = onFiles ? {
    onDragOver: (e) => {
      if (![...(e.dataTransfer?.types || [])].includes('Files')) return;
      e.preventDefault();
      setDropping(true);
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); },
    onDrop: (e) => {
      const files = filesOf(e.dataTransfer);
      setDropping(false);
      if (!files.length) return;
      e.preventDefault();
      onFiles(files);
    },
    onPaste: (e) => {
      // A picture on the clipboard; typed or pasted words go where they were going.
      const files = [...(e.clipboardData?.files || [])].filter((f) => /^image\//.test(f.type));
      if (!files.length) return;
      e.preventDefault();
      onFiles(files);
    }
  } : {};
  const head = useRef(null);
  const closeRef = useRef(on.close);
  closeRef.current = on.close;

  // The panel takes the focus when it opens (its heading - never a field,
  // so a phone's keyboard does not jump up), and gives it back on close.
  useEffect(() => {
    const back = document.activeElement;
    if (head.current) head.current.focus({ preventScroll: true });
    let was = null;
    if (phone) { was = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    return () => {
      if (phone) document.body.style.overflow = was;
      const row = document.querySelector(`.ctd [data-row-id="${window.CSS && CSS.escape ? CSS.escape(String(item.id)) : item.id}"]`);
      const to = row || back;
      if (to && typeof to.focus === 'function' && document.contains(to)) {
        try { to.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    };
    // Once per to-do: the key on the panel changes with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opened from a row's comments, checklist or "2/5": straight to that part.
  useEffect(() => {
    if (!scrollTo || !box.current) return;
    const el = box.current.querySelector(scrollTo === 'parts' ? '[data-parts]'
      : scrollTo === 'subtasks' ? '[data-subtask-section]' : '.tds-tabs');
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    // On open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes (a menu or a dialog in front answers its own first); on a
  // phone Tab stays inside the sheet.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (document.querySelector('.tds-wrap, .tdk-wrap, .ctd-sheet-wrap')) return;
        if (e.target && e.target.closest && e.target.closest('.tda-menu, .tda-pop-panel')) return;
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (!phone || e.key !== 'Tab' || !box.current) return;
      const els = [...box.current.querySelectorAll(FOCUSABLE)].filter((x) => x.offsetParent !== null);
      if (!els.length) return;
      if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phone]);

  const section = list && item.section_id ? (list.sections || []).find((s) => s.id === item.section_id) : null;
  const p = Number(item.priority) || 4;
  const isDone = Boolean(item.done || item.done_at);
  const delegated = item.access === 'delegated';

  return (
    <>
      {phone && <div className="tdsp-scrim" aria-hidden="true" onClick={() => on.close()} />}
      <aside ref={box} className={`tdsp${phone ? ' is-sheet' : ''}${dropping ? ' is-dropping' : ''}`} role="dialog"
             aria-modal={phone ? 'true' : 'false'} aria-labelledby="tdsp-title" data-panel={item.id} {...dropProps}>
        {dropping && <div className="tdsp-drop" aria-hidden="true"><Icon name="paperclip" size={18} /><span>Drop to attach</span></div>}
        <div className="tdsp-head">
          {!delegated && (
            <label className={`ctd-check p${isDone ? 4 : p}`} title={isDone ? 'Not done after all' : 'Done'}>
              <input type="checkbox" checked={isDone || on.ticking} disabled={!editable && !isDone}
                     onChange={() => (isDone ? on.reopen(item) : on.complete(item))}
                     aria-label={isDone ? `Mark "${item.title}" as not done` : `Mark "${item.title}" as done`} />
              <span className="ctd-ring" aria-hidden="true" />
            </label>
          )}
          <p className="tdsp-where">
            {list && (
              <button type="button" className="tdsp-crumb" onClick={() => on.goList(list)} title={`Open ${list.name}`}>
                {list.is_inbox ? <Icon name="inbox" size={13} /> : list.is_private ? <Icon name="lock" size={13} />
                  : <span className={`tda-dot ${swatch(list.color)}`} aria-hidden="true" />}
                <span>{list.name}</span>
                {section && <span className="tdsp-crumb-sec">{` › ${section.name}`}</span>}
              </button>
            )}
            {parent && (
              <button type="button" className="tdsp-crumb is-parent" onClick={() => on.open(parent)} title="Open the to-do this is a sub-task of">
                <Icon name="chevronRight" size={12} /><span>{parent.title}</span>
              </button>
            )}
          </p>
          <span className="tdsp-tools">
            {position && (
              <>
                <button type="button" className="tdsp-icon" aria-label="Previous to-do" title="Previous to-do (K)"
                        disabled={!position.prev} onClick={() => on.step(-1)}><Icon name="arrowUp" size={15} /></button>
                <button type="button" className="tdsp-icon" aria-label="Next to-do" title="Next to-do (J)"
                        disabled={!position.next} onClick={() => on.step(1)}><Icon name="arrowDown" size={15} /></button>
              </>
            )}
            {menu.length > 0 && <MoreMenu label={`More for "${item.title}"`} items={menu} />}
            <button type="button" className="tdsp-icon tdsp-close" aria-label="Close" title="Close (Esc)" onClick={() => on.close()}>
              <Icon name="close" size={17} />
            </button>
          </span>
        </div>
        <h2 id="tdsp-title" className="tdsp-title-sr" ref={head} tabIndex={-1}>{item.title || 'Untitled'}</h2>

        <div className="tdsp-body">
          {editable && !delegated && !isDone ? (
            <TodoEditor key={item.id} item={item} {...editorProps} variant="panel" liveRef={liveRef}
                        onCancel={() => on.close()} />
          ) : (
            <div className="tdsp-read">
              <p className="tdsp-read-title">{item.title}</p>
              {item.notes && <p className="tdsp-notes">{item.notes}</p>}
            </div>
          )}
          {subtasks}
          <div className="tdsp-talk">{detail}</div>
        </div>
      </aside>
    </>
  );
}

/** The panel's ⋯, from what the row's ⋯ held that the panel does not show as a field. */
export const panelMenu = ({ item, withLists, canConvert, on }) => [
  withLists && { label: item.pinned ? 'Unpin' : 'Pin to the top', icon: 'pin', onClick: () => on.pin(item) },
  withLists && { label: 'Colour', icon: 'tag', sub: (close) => (
    <ColorPicker name={`"${item.title}"`} value={item.color} onPick={(c) => { close(); on.color(item, c); }} />) },
  canConvert && { label: 'Convert to note', icon: 'note', onClick: () => on.convert(item) },
  item.repeat && { label: 'Complete forever', icon: 'done', onClick: () => on.forever(item) },
  { sep: true },
  { label: 'Delete', icon: 'trash', danger: true, onClick: () => on.remove(item) }
].filter(Boolean);
